import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#trapFocus);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.addEventListener(DialogCloseEvent.eventName, this.#clearJustAdded);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#trapFocus);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.removeEventListener(DialogCloseEvent.eventName, this.#clearJustAdded);

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  // Sources that dispatch CartAddEvent (all of them, since it doubles as the
  // theme's general-purpose "cart changed" event) without an actual add
  // having happened: the cart's own quantity stepper, and the quick order
  // list's quantity edits and its "remove all" action. None of these should
  // pop the drawer open or claim something was added -- remove-all
  // especially, since it is the exact opposite of an add.
  static #NON_ADD_SOURCES = ['cart-items-component', 'quick-order-quantity', 'quick-order-remove-all'];

  /**
   * CART ADD → store last focus BEFORE opening drawer
   */
  #handleCartAdd = (event) => {
    const { didError, source } = event.detail.data ?? {};

    const isAdd = !didError && !CartDrawerComponent.#NON_ADD_SOURCES.includes(source);

    if (isAdd && this.hasAttribute('auto-open')) {
      this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // Flags the heading as a confirmation ("Added to cart") rather than a
      // plain "Cart", so a drawer that appeared on its own explains itself.
      this.refs.dialog?.setAttribute('data-just-added', 'true');
      this.showDialog();
    }

    const itemCount = event.detail.resource?.item_count;

    if (itemCount === undefined) {
      // An add carries no cart object -- waiting on a second /cart.js request
      // just to learn the count would put a whole round trip back in front of
      // the shopper. The re-rendered cart markup states it instead, so read it
      // once that has landed: the morph runs synchronously inside this same
      // event dispatch, so a microtask is late enough.
      queueMicrotask(() => {
        const counted = this.#countFromMarkup();
        this.#syncEmptyState(counted);
        this.#announceCartCount(counted);
      });
    } else {
      this.#syncEmptyState(itemCount);
      this.#announceCartCount(itemCount);
    }
  };

  /**
   * Reads the cart's item count out of the drawer's own rendered markup.
   *
   * `cart-products` prints it in a hidden element, but only when the cart has
   * something in it -- the empty state has no such element, so its absence is
   * itself the answer rather than a failure to find one.
   *
   * @returns {number | undefined} The count, or undefined if the markup can't say.
   */
  #countFromMarkup() {
    const { dialog } = this.refs;
    if (!dialog) return undefined;

    const countElement = dialog.querySelector('[ref="cartItemCount"]');
    if (!countElement) return dialog.querySelector('.cart-form') ? undefined : 0;

    const parsed = Number.parseInt(countElement.textContent ?? '', 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  /**
   * Keeps the dialog's empty-cart styling in step with the cart.
   *
   * The empty/filled markup lives inside the drawer's hydration region, but the
   * class and label that style it sit on the dialog itself, which a hydration
   * morph never touches. Without this, the first add of a session leaves the
   * populated drawer wearing the centered empty-cart layout.
   *
   * @param {number | undefined} itemCount - The cart's item count.
   */
  #syncEmptyState(itemCount) {
    const { dialog } = this.refs;
    if (!dialog || itemCount === undefined) return;

    const isEmpty = itemCount === 0;
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   */
  #announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * OPEN CART DRAWER → move focus inside drawer
   */
  #trapFocus = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    requestAnimationFrame(() => {
      const focusTarget =
        dialog.querySelector('button, a, input, [tabindex]:not([tabindex="-1"])') || dialog;

      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus();
      }
    });
  };

  /**
   * CLOSE CART DRAWER → the confirmation belongs to that one add, not to the
   * next time the drawer is opened.
   */
  #clearJustAdded = () => {
    this.refs.dialog?.removeAttribute('data-just-added');
  };

  /**
   * CLOSE CART DRAWER → restore focus to trigger
   */
  #restoreFocus = () => {
    requestAnimationFrame(() => {
      if (this.#lastFocusedElement instanceof HTMLElement) {
        this.#lastFocusedElement.focus();
      }
    });
  };

  open() {
    // Opened from the cart icon, so there is nothing to confirm.
    this.refs.dialog?.removeAttribute('data-just-added');
    // Remember the trigger so closing returns focus to it, not to whatever an
    // earlier add left behind.
    this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    // Desktop drawers are tall enough to pin a slightly larger summary and keep Check out on screen.
    const threshold = window.matchMedia('(min-width: 750px)').matches ? 0.65 : this.#summaryThreshold;
    dialog.setAttribute('cart-summary-sticky', ratio > threshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}